import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProductsAnalytics } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { usePageViewAnalytics } from '@client/analytics/hooks';
import { TEALIUM_PAGE_TYPE } from '@client/analytics/types/types';
import { isDefined } from '@client/common/utils/boolean/isDefined';
import { useCheckPermissions } from '@client/context/hooks';
import { WISHLIST_MODAL_TYPE } from '@client/myAccount/wishlists/components/WishlistModal';
import { isAddItemsToCartDisabled as isAddItemsToCartDisabledUtil } from '@client/myAccount/wishlists/utils/wishlist';
import { RemoteRecordError } from 'b2b-apps/core/common/components/RemoteRecordHandler';
import { useAddItemsToCartAction, useRemoveWishlistItemsAction, useWishlistEffect } from 'b2b-apps/core/myAccount/wishlists/hooks';
import { PERMISSION } from 'b2b-common/core/context/Context.types';
import { StandaloneViewPage } from './StandaloneViewPage';

export const StandaloneViewPageContainer = (): ReactElement => {
  const params = useParams<{ wishlistId: string }>();
  const wishlistId = params.wishlistId || '';
  const wishlistRecord = useWishlistEffect(wishlistId);
  const { data, isLoading } = wishlistRecord;
  const hasAccessCartPermissions = useCheckPermissions([PERMISSION.accessCart]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [state, setState] = useState<{ isModalOpened: boolean, modalType: WISHLIST_MODAL_TYPE | null }>({
    isModalOpened: false,
    modalType: null,
  });
  const { analyzeProductsAddToCart } = useProductsAnalytics();
  const addItemsToCartAction = useAddItemsToCartAction();
  const removeWishlistItemsAction = useRemoveWishlistItemsAction();
  const isAddItemsToCartDisabled = isAddItemsToCartDisabledUtil(data, selectedItems);

  const handleCloneClick = (): void => {
    setState({
      modalType: WISHLIST_MODAL_TYPE.clone,
      isModalOpened: true,
    });
  };

  const handleSelectedItemsChange = (selectedItems: string[]): void => {
    setSelectedItems(selectedItems);
  };

  const handleAddItemsToCartClick = (): void => {
    setState({
      modalType: WISHLIST_MODAL_TYPE.addItemsToCart,
      isModalOpened: true,
    });
  };

  const handleModalCloseClick = (): void => {
    setState((prevState) => ({ ...prevState, isModalOpened: false }));
  };

  const handleAddItemsToCart = (wishlistId: string): void => {
    const products = selectedItems.map(selectedItem => {
      const matchingItemIndex = wishlistRecord.data?.items.findIndex(item => item.sku === selectedItem);

      return {
        sku: selectedItem,
        listPosition: isDefined(matchingItemIndex) ? matchingItemIndex + 1 : undefined,
      };
    }) || null;

    analyzeProductsAddToCart({ products, additionalId: wishlistId });
    addItemsToCartAction({ wishlistId, items: selectedItems });
  };

  const handleRemoveItems = (wishlistId: string): void => {
    removeWishlistItemsAction({ wishlistId, skus: selectedItems || [] });
  };

  usePageViewAnalytics({
    remoteRecord: wishlistRecord,
    pageType: TEALIUM_PAGE_TYPE.account,
  });

  return (
    <RemoteRecordError remoteRecords={[wishlistRecord]}>
      <StandaloneViewPage
        isModalOpened={state.isModalOpened}
        modalType={state.modalType}
        hasAccessCartPermissions={hasAccessCartPermissions}
        isAddItemsToCartDisabled={isAddItemsToCartDisabled}
        isLoading={isLoading}
        onAddItemsToCartClick={handleAddItemsToCartClick}
        onCloneClick={handleCloneClick}
        onModalCloseClick={handleModalCloseClick}
        onSelectedItemsChange={handleSelectedItemsChange}
        selectedItems={selectedItems}
        wishlist={wishlistRecord}
        onAddItemsToCart={handleAddItemsToCart}
        onRemoveItems={handleRemoveItems}
      />
    </RemoteRecordError>
  );
};
